import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { AddressTag, ChangeTagsPopover, DocumentTag, GeneralTag, TagsResponse } from '@interfaces'
import { BehaviorSubject, Observable, Subject, map } from 'rxjs'

@Injectable({
	providedIn: 'root'
})
export class TagsService {
	private http = inject(HttpClient)

	url = '/customers/tags'
	documentUrl = '/documents/tags'

	// Observable para la creación de etiquetas generales
	private _creatingGeneralTag: boolean = false
	private creatingGeneralTagSubject = new BehaviorSubject<boolean>(false)
	creatingGeneralTag$ = this.creatingGeneralTagSubject.asObservable()
	// Observable para la creación de etiquetas de documento
	private _creatingDocumentTag: boolean = false
	private creatingDocumentTagSubject = new BehaviorSubject<boolean>(false)
	creatingDocumentTag$ = this.creatingDocumentTagSubject.asObservable()
	// Observable para la creación de etiquetas de direccion
	private _creatingAddressTag: boolean = false
	private creatingAddressTagSubject = new BehaviorSubject<boolean>(false)
	creatingAddressTag$ = this.creatingAddressTagSubject.asObservable()
	// Observable para aplicar los cambios de asignación de etiquetas
	applyTagsSubject = new Subject<ChangeTagsPopover>()
	$applyTags = this.applyTagsSubject.asObservable()
	// Observable para la gestión de etiquetas
	changeTagsSubject = new BehaviorSubject<ChangeTagsPopover>({})
	$changeTags = this.changeTagsSubject.asObservable()
	// Observable para comunicar el multiselector con el popover de añadir etiquetas
	newTagsSubject = new BehaviorSubject<GeneralTag[]>([])
	$newTags = this.newTagsSubject.asObservable()

	// Crea una etiqueta de direccion
	createAddressTag(data: AddressTag): Observable<AddressTag> {
		return this.http.post<AddressTag>(`${this.url}/address`, data)
	}

	// Crea una etiqueta de documento
	createDocumentTag(data: DocumentTag): Observable<DocumentTag> {
		return this.http.post<DocumentTag>(`${this.documentUrl}/document`, data)
	}

	// Crea una etiqueta general
	createGeneralTag(data: GeneralTag): Observable<GeneralTag> {
		return this.http.post<GeneralTag>(`${this.url}/general`, data)
	}

	// Elimina la etiqueta de direccion pasada como parámetro
	deleteAddressTag(id: number): Observable<void> {
		return this.http.delete<void>(`${this.url}/address/${id}`)
	}

	// Elimina la etiqueta de documento pasada como parámetro
	deleteDocumentTag(id: number): Observable<void> {
		return this.http.delete<void>(`${this.documentUrl}/document/${id}`)
	}

	// Elimina la etiqueta general pasada como parámetro
	deleteGeneralTag(id: number): Observable<void> {
		return this.http.delete<void>(`${this.url}/general/${id}`)
	}

	// Obtiene todas las etiquetas de direccion
	getAllAddressTags(orderByCreation = false): Observable<AddressTag[]> {
		return this.http
			.get<TagsResponse>(`${this.url}/address`, { params: { orderByCreation } })
			.pipe(map(response => response.content as AddressTag[]))
	}

	// Obtiene todas las etiquetas de documento
	getAllDocumentTags(orderByCreation = false): Observable<DocumentTag[]> {
		return this.http
			.get<TagsResponse>(`${this.documentUrl}/document`, { params: { orderByCreation } })
			.pipe(map(response => response.content as DocumentTag[]))
	}

	// Obtiene todas las etiquetas generales
	getAllGeneralTags(orderByCreation = false): Observable<GeneralTag[]> {
		return this.http
			.get<TagsResponse>(`${this.url}/general`, { params: { orderByCreation } })
			.pipe(map(response => response.content as GeneralTag[]))
	}

	// Obtiene las etiquetas de dirección
	getAddressTags(page = 0, size = 15, genericFilter = ''): Observable<TagsResponse> {
		return this.http.get<TagsResponse>(`${this.url}/address?page=${page}&size=${size}&genericFilter=${genericFilter}`)
	}

	// Obtiene las etiquetas de documentos
	getDocumentTags(page = 0, size = 15, genericFilter = ''): Observable<TagsResponse> {
		return this.http.get<TagsResponse>(
			`${this.documentUrl}/document?page=${page}&size=${size}&genericFilter=${genericFilter}`
		)
	}

	// Obtiene las etiquetas generales
	getGeneralTags(page = 0, size = 15, genericFilter = ''): Observable<TagsResponse> {
		return this.http.get<TagsResponse>(`${this.url}/general?page=${page}&size=${size}&genericFilter=${genericFilter}`)
	}

	// Resetea el estado de la creación para indicar que ya no se está creando ninguna etiqueta direccion
	resetCreatingAddressState() {
		this._creatingAddressTag = false
		this.creatingAddressTagSubject.next(this._creatingAddressTag)
	}

	// Resetea el estado de la creación para indicar que ya no se está creando ninguna etiqueta doucmento
	resetCreatingDocumentState() {
		this._creatingDocumentTag = false
		this.creatingDocumentTagSubject.next(this._creatingDocumentTag)
	}

	// Resetea el estado de la creación para indicar que ya no se está creando ninguna etiqueta general
	resetCreatingGeneralState() {
		this._creatingGeneralTag = false
		this.creatingGeneralTagSubject.next(this._creatingGeneralTag)
	}

	// Cambia el estado de si se está creando una etiqueta direccion
	toogleCreatingAddress() {
		this._creatingAddressTag = !this._creatingAddressTag
		this.creatingAddressTagSubject.next(this._creatingAddressTag)
	}

	// Cambia el estado de si se está creando una etiqueta documento
	toogleCreatingDocument() {
		this._creatingDocumentTag = !this._creatingDocumentTag
		this.creatingDocumentTagSubject.next(this._creatingDocumentTag)
	}

	// Cambia el estado de si se está creando una etiqueta general
	toogleCreatingGeneral() {
		this._creatingGeneralTag = !this._creatingGeneralTag
		this.creatingGeneralTagSubject.next(this._creatingGeneralTag)
	}

	// Modifica el color de una etiqueta general
	updateColour(id: number, code: string): Observable<GeneralTag> {
		let colour = code ?? '#808080'
		colour = colour.replace('#', '%23')
		return this.http.put<GeneralTag>(`${this.url}/general/${id}?colour=${colour}`, {})
	}
}
